import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
  SimpleChanges,
  viewChild
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { concat, unionBy } from 'lodash';
import { Subscription } from 'rxjs';
import { cloneDeepObj } from 'src/app/core/utils/util';
import { PhoneBookRecipientRequest } from '../../../../core/api/models';
import { TranslationModule } from '../../../../i18n/translation.module';
import { PhonebookAdvanceSearchComponent } from '../phonebook-advance-search/phonebook-advance-search.component';
import { PhonebookListComponent } from '../phonebook-list/phonebook-list.component';
import {
  PhonebookCustomDto,
  PhonebookGlobalIndex,
} from '../shared/phonebook-custom.model';
import { PhonebookStateService } from '../shared/phonebook-state.service';
import { ShareModule } from './../../../../shared/share.module';
import { PhonebookGlobalIndexComponent } from './../phonebook-global-index/phonebook-global-index.component';
import { SelectedPhonebooksComponent } from './../selected-phonebooks/selected-phonebooks.component';
import { RemoveEmptyContactsPipe } from './remove-empty-contacts.pipe';
import { BaseFormModule } from 'src/app/shared/components/base/base-component/base-form.module';
@Component({
    selector: 'phonebook-selector',
    imports: [
        PhonebookGlobalIndexComponent,
        PhonebookAdvanceSearchComponent,
        PhonebookListComponent,
        MatTooltipModule,
        RemoveEmptyContactsPipe,
        BaseFormModule,
    ],
    templateUrl: './phonebook-selector.component.html'
})
export class PhonebookSelectorComponent {
  //#region props
  phonebookStateService = inject(PhonebookStateService);
  dialogService = inject(MatDialog);
  readonly PhonebookListComponent = viewChild(PhonebookListComponent);

  @Input() justCurrentOrganization: boolean = false;
  @Input() isShowGlobalIndex: boolean = true;
  @Input() isShowInputs: boolean = true;
  @Input() isShowParentBtn: boolean = true;
  @Input() isValidGlobalIndex: boolean = false;
  @Input() isWhiteMessagePhonebook: boolean;
  @Input() privateNumber: any;
  @Input() phonebookRecipients: PhonebookCustomDto[] = [];
  recipients: PhoneBookRecipientRequest[] = [];
  phonebookIds: number[] = [];
  phonebookSelectedIds: number[] = [];
  searchedRecipients: PhonebookCustomDto[] = [];
  searchedPhonebookIds: number[] = [];
  globalIndex: PhonebookGlobalIndex;
  @Output() recipientsChange = new EventEmitter<PhoneBookRecipientRequest[]>();
  @Output() phonebookIdsChange = new EventEmitter<number[]>();
  @Output() globalIndexChange = new EventEmitter<PhonebookGlobalIndex>();
  sub: Subscription = null;
  //#endregion

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.callRefresh) this.onRefresh();
  }

  ngAfterViewInit(): void {
    if (this.phonebookRecipients?.length) this.applyPhonebookRecipients();
  }

  applyPhonebookRecipients() {
    this.sub = this.PhonebookListComponent().items$.subscribe((res) => {
      const PhonebookListComponentValue = this.PhonebookListComponent();
      PhonebookListComponentValue.phonebookRecipients =
        this.phonebookRecipients;
      PhonebookListComponentValue.selectedPhonebooks = this.phonebookRecipients;
      PhonebookListComponentValue.changePhonebookRecipients();
      PhonebookListComponentValue.emit();
    });
  }

  //#region getter
  getRecipients(event: PhoneBookRecipientRequest[]) {
    this.phonebookRecipients = cloneDeepObj(event);
    this.mergeAndEmitRecipients();
  }

  getPhonebookIds(event: number[]) {
    this.phonebookSelectedIds = cloneDeepObj(event);
    this.mergeAndEmitPhonebookIds();
  }
  getSearchedRecipients(event: PhoneBookRecipientRequest[]) {
    this.searchedRecipients = cloneDeepObj(event);
    this.mergeAndEmitRecipients();
  }
  getSearchedPhonebookIds(event: number[]) {
    this.searchedPhonebookIds = cloneDeepObj(event);
    this.mergeAndEmitPhonebookIds();
  }
  //#endregion

  //#region merge and emit

  mergeAndEmitRecipients() {
    this.recipients = unionBy(
      this.searchedRecipients,
      this.phonebookRecipients,
      'id'
    );
    const isValid = this.phonebookStateService.isValidSelectedRecipients(
      this.recipients
    );
    const isEmptyGlobalIndex =
      !this.globalIndex?.fromIndex &&
      !this.globalIndex?.count &&
      +this.globalIndex?.fromIndex !== 0 &&
      +this.globalIndex?.count !== 0;
    if ((isValid && isEmptyGlobalIndex) || this.isValidGlobalIndex)
      this.recipientsChange.emit(
        this.phonebookStateService.fixPhonebooks(this.recipients)
      );
    else this.recipientsChange.emit(null);
  }

  mergeAndEmitPhonebookIds() {
    this.phonebookIds = concat(
      this.searchedPhonebookIds,
      this.phonebookSelectedIds
    );
    this.phonebookIdsChange.emit(this.phonebookIds);
  }

  emitGlobalIndex(event: PhonebookGlobalIndex) {
    this.globalIndex = event;
    this.globalIndexChange.emit(event);
    this.mergeAndEmitRecipients();
  }

  //#endregion

  openSelectedPhonebooksDialog(recipients: PhoneBookRecipientRequest[]) {
    if (recipients?.length)
      this.dialogService.open(SelectedPhonebooksComponent, {
        data: recipients,
      });
  }

  onRefresh() {
    this.PhonebookListComponent().getTableData();
  }

  ngOnDestroy(): void {
    if (this.sub) this.sub.unsubscribe();
  }
}
